/* eslint-disable consistent-return */
/* eslint-disable no-console */
import {
    useEffect, useRef, useState, useCallback, useMemo,
} from 'react';
import { useQuery } from '@apollo/client';
import * as Schema from '@sellermodules/order/services/graphql/schema';
import formatDate, { getMinDate } from '@helper_date';
import { storeConfigVar } from '@services/graphql/cache';

const INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

const useOrderWatcher = (options = {}) => {
    const { enabled = true } = options;
    const intervalRef = useRef(null);
    const isFirstRun = useRef(true);
    const [showNotification, setShowNotification] = useState(false);
    const [unconfirmedCount, setUnconfirmedCount] = useState(0);

    const getFilterVariables = useCallback(() => {
        const currentPeriod = storeConfigVar()?.default_order_grid_period_filter || 3;
        return {
            filter: {
                order_date: {
                    from: formatDate(getMinDate(currentPeriod), 'YYYY-MM-DD 00:00:00'),
                    to: formatDate(new Date(), 'YYYY-MM-DD 23:59:59'),
                },
            },
        };
    }, []);

    const variables = useMemo(() => getFilterVariables(), [getFilterVariables]);

    const {
        data, refetch,
    } = useQuery(Schema.getSellerOrderMeta, {
        variables,
        context: { request: 'internal' },
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
        skip: !enabled,
    });

    const playNotificationSound = () => {
        try {
            const audio = new Audio('/assets/audio/beep.mp3');
            audio.play().catch((err) => console.log('Audio play failed:', err));
        } catch (error) {
            console.log('Audio init failed:', error);
        }
    };

    const checkAndNotify = useCallback((count) => {
        try {
            if (count !== undefined && count !== null) {
                const numericCount = parseInt(count, 10) || 0;
                setUnconfirmedCount(numericCount);

                if (numericCount > 0) {
                    setShowNotification(true);
                    if (isFirstRun.current) {
                        isFirstRun.current = false;
                    } else {
                        playNotificationSound();
                    }
                } else {
                    setShowNotification(false);
                    isFirstRun.current = false;
                }
            } else {
                setShowNotification(false);
            }
        } catch (error) {
            console.error('Error checking orders:', error);
        }
    }, []);

    const handleCloseNotification = useCallback(() => {
        setShowNotification(false);
    }, []);

    useEffect(() => {
        if (!enabled) return;

        if (data) {
            const count = data?.getSellerOrderMeta?.order?.new_order?.value;
            checkAndNotify(count);
        }
    }, [data, enabled, checkAndNotify]);

    useEffect(() => {
        if (!enabled) return;

        intervalRef.current = setInterval(async () => {
            if (refetch) {
                try {
                    const freshFilterVars = getFilterVariables();
                    const res = await refetch(freshFilterVars);
                    const count = res?.data?.getSellerOrderMeta?.order?.new_order?.value;
                    checkAndNotify(count);
                } catch (err) {
                    console.error('Error checking orders on interval:', err);
                }
            }
        }, INTERVAL_MS);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [refetch, enabled, checkAndNotify, getFilterVariables]);

    return {
        showNotification,
        unconfirmedCount,
        handleCloseNotification,
    };
};

export default useOrderWatcher;
